import React from 'react';

export const SkeletonBox = ({ width = '100%', height = '20px', style = {} }) => (
  <div 
    style={{ 
      width, 
      height, 
      backgroundColor: '#F0F0F0', 
      borderRadius: '4px',
      animation: 'skeleton-pulse 1.5s ease-in-out infinite',
      ...style 
    }} 
  />
);

export const SkeletonCard = ({ children, style = {} }) => (
  <div 
    style={{ 
      backgroundColor: '#FFF', 
      borderRadius: '12px', 
      border: '1px solid #E0E0E0', 
      padding: '24px',
      ...style 
    }}
  >
    {children}
  </div>
);

export const SkeletonTable = ({ rows = 5, columns = 5 }) => (
  <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ backgroundColor: '#F5F5F5', borderBottom: '2px solid #E0E0E0' }}>
          {Array(columns).fill(0).map((_, i) => (
            <th key={i} style={{ padding: '16px', textAlign: 'left' }}>
              <SkeletonBox height="16px" width="80%" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array(rows).fill(0).map((_, rowIdx) => (
          <tr key={rowIdx} style={{ borderBottom: '1px solid #F0F0F0' }}>
            {Array(columns).fill(0).map((_, colIdx) => (
              <td key={colIdx} style={{ padding: '16px' }}>
                <SkeletonBox height="14px" width={colIdx === 0 ? '60%' : '80%'} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SkeletonDashboard = () => (
  <div style={{ padding: '24px' }}>
    <SkeletonBox width="200px" height="32px" style={{ marginBottom: '24px' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
      {Array(3).fill(0).map((_, i) => (
        <SkeletonCard key={i}>
          <SkeletonBox width="120px" height="16px" style={{ marginBottom: '12px' }} />
          <SkeletonBox width="60px" height="36px" />
        </SkeletonCard>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
      {Array(2).fill(0).map((_, i) => (
        <SkeletonCard key={i}>
          <SkeletonBox width="150px" height="20px" style={{ marginBottom: '16px' }} />
          {Array(5).fill(0).map((_, j) => (
            <SkeletonBox key={j} height="40px" style={{ marginBottom: '8px' }} />
          ))}
        </SkeletonCard>
      ))}
    </div>
  </div>
);

export const SkeletonDetailPage = () => (
  <div style={{ padding: '24px' }}>
    <SkeletonBox width="120px" height="20px" style={{ marginBottom: '24px' }} />
    <SkeletonCard style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <SkeletonBox width="300px" height="28px" />
        <div style={{ display: 'flex', gap: '8px' }}>
          <SkeletonBox width="40px" height="40px" />
          <SkeletonBox width="40px" height="40px" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
        {Array(4).fill(0).map((_, i) => (
          <div key={i}>
            <SkeletonBox width="80px" height="14px" style={{ marginBottom: '6px' }} />
            <SkeletonBox width="120px" height="18px" />
          </div>
        ))}
      </div>
    </SkeletonCard>
    <SkeletonCard>
      <SkeletonBox width="150px" height="20px" style={{ marginBottom: '16px' }} />
      {Array(3).fill(0).map((_, i) => (
        <SkeletonBox key={i} height="60px" style={{ marginBottom: '12px' }} />
      ))}
    </SkeletonCard>
  </div>
);

// CSS animace
const styleTag = document.createElement('style');
styleTag.innerHTML = `
@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
`;
document.head.appendChild(styleTag);

export default SkeletonBox;

// Skeleton pro Zákazníky/Trenažéry (tabulka s filtry)
export const SkeletonMachinesCustomers = () => (
  <div style={{ padding: '24px' }}>
    <SkeletonBox width="200px" height="28px" style={{ marginBottom: '24px' }} />
    
    {/* Filtry */}
    <SkeletonCard style={{ marginBottom: '24px', padding: '20px' }}>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <SkeletonBox width="300px" height="42px" />
        <SkeletonBox width="150px" height="42px" />
        <SkeletonBox width="150px" height="42px" />
      </div>
    </SkeletonCard>
    
    {/* Tabulka */}
    <SkeletonTable rows={8} columns={6} />
  </div>
);

// Skeleton pro Šablony (grid cards)
export const SkeletonTemplates = () => (
  <div style={{ padding: '24px' }}>
    <SkeletonBox width="200px" height="28px" style={{ marginBottom: '24px' }} />
    
    {/* Info box */}
    <SkeletonCard style={{ marginBottom: '24px', padding: '16px' }}>
      <SkeletonBox width="80%" height="16px" />
    </SkeletonCard>
    
    {/* Grid cards */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
      {Array(6).fill(0).map((_, i) => (
        <SkeletonCard key={i}>
          <SkeletonBox width="150px" height="24px" style={{ marginBottom: '12px' }} />
          <SkeletonBox width="100px" height="16px" style={{ marginBottom: '8px' }} />
          <SkeletonBox width="80%" height="14px" style={{ marginBottom: '16px' }} />
          <SkeletonBox width="100%" height="40px" />
        </SkeletonCard>
      ))}
    </div>
  </div>
);
